import { Lightning } from '@lightningjs/sdk';
import { getFontFaceFromStyle, withSeparator } from 'support/textUtils';
import { LiveEvent, ShowDetails } from 'types/api/media';
import RatingBadge, { MEDIUM_SIZE } from 'components/common/RatingBadge';
import {
  getDurationHourMinutesNoSpaceFormatted,
  isLiveEvent,
  isMovie,
} from 'support/contentUtils';
import TextLabel from './TextLabel';

export interface MetadataLabelTemplateSpec
  extends Lightning.Component.TemplateSpec {
  mediaContent: ShowDetails | LiveEvent | null;
  shouldCenter: boolean;

  LabelContainer: {
    RatingBadge: typeof RatingBadge;
    Info: typeof TextLabel;
  };
}

const FONT_SIZE = 29;
const RATING_BADGE_MARGIN = 30;
const HEIGHT = MEDIUM_SIZE.HEIGHT; // Match the size of the rating badge to avoid pixel rendering cutoffs

export default class MetadataLabel
  extends Lightning.Component<MetadataLabelTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<MetadataLabelTemplateSpec>
{
  private _LabelContainer = this.getByRef('LabelContainer')!;
  private _RatingBadge = this._LabelContainer.getByRef('RatingBadge')!;
  private _Info = this._LabelContainer.getByRef('Info')!;
  private _shouldCenter = false;

  private _metaDataText = '';

  static override _template(): Lightning.Component.Template<MetadataLabelTemplateSpec> {
    return {
      h: HEIGHT,
      LabelContainer: {
        flex: {
          direction: 'row',
          alignItems: 'center',
        },
        RatingBadge: {
          flexItem: { marginRight: RATING_BADGE_MARGIN },
          size: 'medium',
          type: RatingBadge,
          roundedRect: true,
          visible: false,
        },
        Info: {
          type: TextLabel,
          text: {
            h: MEDIUM_SIZE.HEIGHT,
            offsetY: MEDIUM_SIZE.FONT_SIZE + MEDIUM_SIZE.PADDING_Y,
            fontSize: FONT_SIZE,
            fontFace: getFontFaceFromStyle('regular'),
          },
        },
      },
    };
  }

  get title() {
    return this._metaDataText;
  }

  set shouldCenter(shouldCenter: boolean) {
    this._shouldCenter = shouldCenter;
  }

  set mediaContent(mediaContent: ShowDetails | LiveEvent | null) {
    if (!mediaContent) return;

    let infoText = '';
    if (isLiveEvent(mediaContent)) {
      this._RatingBadge.patch({ visible: false });
      infoText = (mediaContent as LiveEvent).tuneIn;
      this._metaDataText = infoText;
    } else {
      const { rating, activeYears, releaseYear, genres } =
        mediaContent as ShowDetails;
      const genresText = genres.join(', ');

      this._RatingBadge.patch({ visible: true, rating });
      this._Info.patch({ text: { fontFace: getFontFaceFromStyle('regular') } });

      if (isMovie(mediaContent)) {
        const durationMinutes = getDurationHourMinutesNoSpaceFormatted(
          mediaContent as ShowDetails,
        );
        infoText = withSeparator(releaseYear, durationMinutes, genresText);
      } else {
        infoText = withSeparator(activeYears, genresText);
      }
      this._metaDataText = withSeparator(rating, infoText);
    }

    this._Info.patch({ text: { text: infoText } });
  }

  override _setup() {
    super._setup();
    this._Info.onAfterUpdate = () => {
      this.centerContainer();
    };
  }

  /* 
  When updating the text component with RoundedRectangle shader while centering it, it seems that LightningJS struggles to position/render the stroke properly.
  Workaround is to manually position it onAfterUpdate.
  */
  private centerContainer() {
    if (!this._shouldCenter) return;
    this.patch({
      x: Math.round((this.w - this._LabelContainer.finalW) / 2),
    });
  }
}
